import { computed, inject, Injectable } from '@angular/core'
import { AppConfigService } from '@mos/client/shared/app-config'
import { AuthRepository } from './auth.repository'

/**
 * Langue de référence du contenu : celle sur laquelle `getI18nValue` retombe côté serveur quand une
 * traduction manque. Toute autre langue de travail est, par construction, une langue de traduction.
 */
const SOURCE_LANGUAGE = 'fr'

/**
 * Expose l'état du rôle Traducteur pour le reste de l'admin - lu par les formulaires admin (verrouillage
 * des champs non-i18n, voir lockNonI18nControls). Vit dans ce lib partagé (et non libs/client/admin/auth)
 * car du code partagé (ex: les formulaires scene, aussi utilisés par mobile) peut en avoir besoin.
 *
 * Il n'y a volontairement PAS de "langue de travail" séparée de la langue générale de l'admin (le
 * sélecteur FR/EN du menu, NavbarRepository/AppConfigService.locale) : un traducteur utilise le MÊME
 * sélecteur qu'un admin pour choisir quelle langue il voit/édite - juste en FR (langue non assignée) tout
 * reste en lecture seule (voir canEditI18nFields), et en EN (assignée) les champs i18n redeviennent
 * éditables. Avoir eu un second sélecteur dédié qui l'emportait silencieusement sur celui-ci dans
 * ApiInterceptor a été une régression prod (le menu affichait FR mais X-APP-LANG partait quand même en EN).
 */
@Injectable({ providedIn: 'root' })
export class TranslatorAccessService {
	private readonly authRepository = inject(AuthRepository)
	private readonly appConfigService = inject(AppConfigService)
	// AuthRepository.player is a getter over its internal signal (see AuthRepository.authState), so reading
	// it here is tracked like any other signal read - computed() follows signal reads through the whole
	// synchronous call stack, not just the literal call site.
	private readonly player = computed(() => this.authRepository.player)

	readonly isTranslator = computed(() => {
		const player = this.player()
		return !!player && !player.isAdmin && !!player.translatorLanguages?.length
	})

	/** Alias explicite : jamais true pour un admin, uniquement pour distinguer l'intention au call site. */
	readonly isReadOnlyMode = this.isTranslator

	readonly assignedLanguages = computed(() => this.player()?.translatorLanguages ?? [])

	/**
	 * true si la langue actuellement sélectionnée dans le menu admin (celle qui part dans X-APP-LANG, voir
	 * ApiInterceptor) fait partie des langues assignées au traducteur - seul ce cas autorise l'édition des
	 * champs i18n (voir lockNonI18nControls).
	 *
	 * `getLocale()` lit un signal (voir AppConfigService.currentLocale), ce qui est indispensable ici :
	 * la locale persistée du menu est restaurée de façon ASYNCHRONE (persistState + StorageStrategy), donc
	 * ce calcul est très souvent évalué une première fois sur la locale par défaut, avant que celle du
	 * joueur n'arrive. Sur un simple champ, le computed serait resté figé sur cette première valeur : dans
	 * le mauvais sens, les formulaires restaient éditables alors que X-APP-LANG partait dans une langue non
	 * assignée - le traducteur saisissait du texte que le serveur annulait ensuite en silence.
	 */
	readonly canEditI18nFields = computed(() => this.assignedLanguages().includes(this.appConfigService.getLocale()))

	/**
	 * La langue de travail du menu admin n'est pas la langue source : on édite une traduction.
	 *
	 * L'API sert les champs i18n réduits à une seule chaîne (I18n.translate()), et retombe sur le français
	 * quand la langue cible est vide - c'est ce repli qui donne le contexte FR dans les formulaires. Le prix
	 * à payer, c'est que le client ne peut pas distinguer « traduit » de « pas encore traduit », donc toute
	 * reconstruction de structure (changer le type d'une scène, le point de vue, ajouter/retirer une ligne)
	 * repart au serveur avec les textes de repli et écrase la traduction en cours.
	 *
	 * D'où le verrou : en langue de traduction, on ne touche qu'au texte i18n déclaré, jamais à la structure.
	 */
	readonly isEditingTranslation = computed(() => this.appConfigService.getLocale() !== SOURCE_LANGUAGE)

	/**
	 * Structure verrouillée : pour un traducteur toujours (il n'a de droits que sur le texte, quelle que soit
	 * la langue affichée), et pour n'importe qui - admin compris - dès qu'il travaille dans une langue de
	 * traduction. Le serveur, lui, ne borne que les traducteurs : sur ce point l'UI est le seul garde-fou.
	 */
	readonly isStructureLocked = computed(() => this.isTranslator() || this.isEditingTranslation())

	/**
	 * Droit d'écrire le texte i18n déclaré : un admin l'a toujours, un traducteur seulement dans une langue
	 * qui lui est assignée - sinon il saisirait du texte que le serveur annulerait en silence.
	 */
	readonly canWriteI18nText = computed(() => !this.isTranslator() || this.canEditI18nFields())
}
